import { defineStore } from "pinia";
import { getDiscountedPrice } from "@/helpers/pricing";
import { canAddProductDuringSeason } from "@/helpers/season.js";
import { useFastDeliveryCartStore } from "./fastDeliveryCart.store";
import { useProductsStore } from "./products.store";
import { routerHolder } from "@/router/instance.js";
import { lsGet, lsGetJson } from "@/helpers/storage.js";

export const useCartStore = defineStore({
    id: "store",

    state: () => ({
        products: lsGet("cart_tazj_product") ? lsGetJson("cart_tazj_product", []) : [],
        showWarningPopup: false, // State variable for the warning popup
        DeliveryConflict: false, // New state variable for delivery conflict
        DeliveryType: "",
        lastOrderId: null,
        checkoutId: null,
        integrity: null,
        onlinePayMentMethod: "visa",
        isSeasonal:false,
        modalState: {
            isOpen: false,
            message: "",
            confirmAction: null,
            cancelAction: null,
        },
    }),

    getters: {
        productGroupById: (state) => {
            return state.products.reduce((acc, obj) => {
                const group = acc.find((group) => group.id === obj.id);
                if (!group) {
                    acc.push(obj);
                }
                return acc;
            }, []);
        },


        totalPrice: (state) => {
            const subtotal = state.products.reduce((total, product) => {
                const price = typeof product.minimum_price === "number" && product.minimum_price > 0
                    ? product.minimum_price
                    : 0;

                const discountPercent = typeof product.discount_percent === "number" && product.discount_percent >= 0
                    ? product.discount_percent
                    : 0;

                const discountedPrice = parseFloat(getDiscountedPrice(price, discountPercent));

                return total + discountedPrice;
            }, 0);

            return subtotal.toFixed(2);
        },

    },

    actions: {
        addToCart(product, isSeasonal = false) {
            const fastDeliveryCart = useFastDeliveryCartStore();
            const productsStore = useProductsStore();
            const seasonCheck = canAddProductDuringSeason(isSeasonal, productsStore.seasonDays);
            if (!seasonCheck.ok) {
                this.modalState = {
                    isOpen: true,
                    message: seasonCheck.message,
                    confirmAction: () => {
                        this.modalState.isOpen = false;
                    },
                    cancelAction: () => {
                        this.modalState.isOpen = false;
                    },
                };
                return false;
            }
            if (fastDeliveryCart.products.length > 0) {
                this.triggerCartConflictModal(product, fastDeliveryCart);
                return false;
            } else {
                // Check if the product is seasonal
                if (isSeasonal) {
                    // Only allow adding if all products in the cart are the same product or the cart is empty
                    const hasDifferentSeasonal = this.products.some(
                        cartProduct => cartProduct.id !== product.id
                    );
                    if (hasDifferentSeasonal) {
                        this.triggerCartConflictModalWithSeasonalProduct(product);
                        return false;
                    }
                    if (!this.isSeasonal && this.products.length > 0) {
                        this.triggerSeasonalCartConflictModalWithProduct(product);
                        return false;
                    } else {
                        this.isSeasonal = true; // Set the seasonal flag
                        productsStore.isFastDelivery = false; // Set fast delivery flag to false
                        this.products.push(product);
                        this.updateLocalStorage();
                        this.checkForDeliveryConflict(); // Check for delivery conflict after adding
                        return true;
                    }
                } else {
                    // If the product is not seasonal, check if there are any seasonal products in the cart
                    if (this.isSeasonal && this.products.length > 0) {
                        this.triggerCartConflictModalWithSeasonalProduct(product);
                        return false;
                    } else {
                        this.isSeasonal = false; // Set the seasonal flag
                        productsStore.isFastDelivery = false; // Set fast delivery flag to false
                        this.products.push(product);
                        this.updateLocalStorage();
                        this.checkForDeliveryConflict(); // Check for delivery conflict after adding
                        return true;
                    }
                }
            }
        },

        removeFromCart(id) {
            const indexToRemove = this.products.findIndex(
                (product) => product.id == id
            );

            if (indexToRemove !== -1) {
                this.products.splice(indexToRemove, 1);
                this.updateLocalStorage();
                this.checkForDeliveryConflict(); // Check for delivery conflict after removing
            }

              if (this.products.length === 0) {
                useProductsStore().isSeasonal = false; // Reset the seasonal flag when clearing the cart
            }
        },

        removeItem(id) {
            this.products = this.products.filter((product) => product.id !== id);
            this.updateLocalStorage();
            this.checkForDeliveryConflict();
            if (this.products.length === 0) {
                useProductsStore().isSeasonal = false; // Reset the seasonal flag when clearing the cart
            }
        },

        removeItemByVariant(productId, variantId) {
            this.products = this.products.filter((product) => {
                // Remove only if both id and variant id match
                if (product.id === productId) {
                    // If product has variants array, check variant id
                    if (product.variants && product.variants.length > 0) {
                        return product.variants[0].id !== variantId;
                    }
                    // If no variants, fallback to not removing
                    return true;
                }
                // Keep products with different id
                return true;
            });
            this.updateLocalStorage();
            this.checkForDeliveryConflict && this.checkForDeliveryConflict();
            if (this.products.length === 0) {
                useProductsStore().isSeasonal = false;
            }
        },

        editCartItem(updatedProduct) {
            const indexToEdit = this.products.findIndex(
                (product) => product.id == updatedProduct.id
            );
            if (indexToEdit !== -1) {
                this.products[indexToEdit] = {
                    ...this.products[indexToEdit],
                    ...updatedProduct,
                };
                this.updateLocalStorage();
                this.checkForDeliveryConflict(); // Check for delivery conflict after editing
            }
        },

        clearCart() {
            this.products = [];
            this.showWarningPopup = false; // Reset the warning popup state when clearing the cart
            this.DeliveryConflict = false; // Reset delivery conflict state
            this.updateLocalStorage();
            useProductsStore().isSeasonal = false; // Reset seasonal flag
        },

        updateLocalStorage() {
            localStorage.setItem("cart_tazj_product", JSON.stringify(this.products));
        },

        checkForDeliveryConflict() {
            if (this.products.length === 0) {
                this.DeliveryConflict = false; // No products, no conflict
                this.showWarningPopup = false; // No conflict, hide popup
                return;
            }

            // Get all fast delivery statuses from the products
            const fastDeliveryStatuses = this.products.map((p) => p.is_fastdelivery);

            // Check if there's at least one fast delivery and one non-fast delivery product
            const hasFastDelivery = fastDeliveryStatuses.includes(true);
            const hasNormalDelivery = fastDeliveryStatuses.includes(false);

            // Set DeliveryConflict and showWarningPopup based on delivery conflict
            this.DeliveryConflict = hasFastDelivery && hasNormalDelivery;
            this.showWarningPopup = this.DeliveryConflict; // Show popup if there's a conflict
        },
        checkDeliveryType() {
            if (this.products.length === 0) {
                return;
            }

            // Get all fast delivery statuses from the products
            const fastDeliveryStatuses = this.products.map((p) => p.is_fastdelivery);

            // Check if there's at least one fast delivery and one non-fast delivery product
            const hasFastDelivery = fastDeliveryStatuses.includes(true);
            const hasNormalDelivery = fastDeliveryStatuses.includes(false);

            if (hasFastDelivery) {
                this.DeliveryType = "FastDelivery";
            } else if (hasNormalDelivery) {
                this.DeliveryType = "NormalDelivery";
            }
        },

        triggerCartConflictModal(product, fastDeliveryCart) {
            this.modalState = {
                isOpen: true,
                message: "لا يمكنك إضافة منتجات سريعة التوصيل مع منتجات عادية, هل تريد تفريغ المنتجات التوصيل السريعة من السلة؟",
                confirmAction: () => {
                    fastDeliveryCart.clearCart();
                    useProductsStore().isFastDelivery = false;
                    this.products.push(product);
                    this.updateLocalStorage();
                    useProductsStore().isFastDelivery = false;
                    this.modalState.isOpen = false;
                },
                cancelAction: () => {
                    this.modalState.isOpen = false;
                },
            };
        },


        triggerCartConflictModalWithSeasonalProduct(product) {
            this.modalState = {
                isOpen: true,
                message: "لا يمكنك إضافة منتجات موسمية مثل أضاحي العيد مع منتجات عادية, هل تريد تفريغ المنتجات الموسمية من السلة؟",
                confirmAction: () => {
                    this.clearCart();
                    this.isSeasonal = false;
                    this.products.push(product);
                    this.updateLocalStorage();
                    this.modalState.isOpen = false;
                    useProductsStore().isSeasonal = false; // Set seasonal flag to false
                    routerHolder.router?.push("/cart");

                },
                cancelAction: () => {
                    this.modalState.isOpen = false;
                },
            };
        },

        triggerSeasonalCartConflictModalWithProduct(product) {
            this.modalState = {
                isOpen: true,
                message: "لا يمكنك إضافة منتجات موسمية مثل أضاحي العيد مع منتجات عادية, هل تريد تفريغ المنتجات العادية من السلة؟",
                confirmAction: () => {
                    this.clearCart(); // Clear the cart instead of product.clearCart()
                    this.isSeasonal = true;
                    this.products.push(product);
                    this.updateLocalStorage();
                    this.modalState.isOpen = false;
                    useProductsStore().isSeasonal = true; // Set seasonal flag to true
                    routerHolder.router?.push("/cart");
                },
                cancelAction: () => {
                    this.modalState.isOpen = false;
                },
            };
        },

        hasSeasonalProducts() {
            return this.products.some(product => product.is_seasonal === true);
        },
    },
});
